
// Pure Math Logic for Quantitative Analysis

// --- PERFORMANCE ATTRIBUTION (Brinson Model) ---

export interface AttributionResult {
    sector: string;
    allocationEffect: number;
    selectionEffect: number;
    interactionEffect: number;
    totalEffect: number;
}

export const calculateBrinsonAttribution = (
    portfolio: { sector: string; weight: number; return: number }[],
    benchmark: { sector: string; weight: number; return: number }[]
): AttributionResult[] => {
    // Group portfolio by sector first if not already aggregated
    // For this engine, we assume inputs are already aggregated by sector
    
    return portfolio.map(portSector => {
        const benchSector = benchmark.find(b => b.sector === portSector.sector) || { weight: 0, return: 0 };
        
        const wP = portSector.weight / 100; // Portfolio Weight
        const wB = benchSector.weight / 100; // Benchmark Weight
        const rP = portSector.return / 100; // Portfolio Return
        const rB = benchSector.return / 100; // Benchmark Return

        // 1. Allocation Effect: (Wp - Wb) * Rb
        // Did we overweight a winning sector?
        const alloc = (wP - wB) * rB;

        // 2. Selection Effect: Wb * (Rp - Rb)
        // Did we pick better stocks than the benchmark in this sector?
        const select = wB * (rP - rB);

        // 3. Interaction Effect: (Wp - Wb) * (Rp - Rb)
        // The compounding effect of overweighting a sector AND picking winners
        const interact = (wP - wB) * (rP - rB);

        return {
            sector: portSector.sector,
            allocationEffect: alloc * 100,
            selectionEffect: select * 100,
            interactionEffect: interact * 100,
            totalEffect: (alloc + select + interact) * 100
        };
    });
};

// --- EXISTING FUNCTIONS BELOW ---

// --- STATISTICAL HELPERS ---

// Standard Normal Cumulative Distribution Function (CDF)
const normalCDF = (x: number): number => {
    var t = 1 / (1 + 0.2316419 * Math.abs(x));
    var d = 0.3989423 * Math.exp(-x * x / 2);
    var prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    if (x > 0) prob = 1 - prob;
    return prob;
};

// Standard Normal Probability Density Function (PDF)
const normalPDF = (x: number): number => {
    return (1.0 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x);
};

// --- BLACK-SCHOLES ENGINE (Client-Side) ---

export interface BSResult {
    price: number;
    delta: number;
    gamma: number;
    vega: number;
    theta: number;
    rho: number;
}

export const calculateBlackScholes = (
    S: number, // Spot
    K: number, // Strike
    T: number, // Time (Years)
    r: number, // Risk-free Rate (decimal)
    sigma: number, // Volatility (decimal)
    type: 'call' | 'put'
): BSResult => {
    if (T <= 0) {
        const price = type === 'call' ? Math.max(0, S - K) : Math.max(0, K - S);
        return { price, delta: 0, gamma: 0, vega: 0, theta: 0, rho: 0 };
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    let price = 0;
    let delta = 0;
    let theta = 0;
    let rho = 0;
    
    const nd1 = normalCDF(d1);
    const nd2 = normalCDF(d2);
    const npd1 = normalPDF(d1);
    const sqrtT = Math.sqrt(T);
    const ert = Math.exp(-r * T);

    if (type === 'call') {
        price = S * nd1 - K * ert * nd2;
        delta = nd1;
        rho = K * T * ert * nd2;
        theta = (- (S * npd1 * sigma) / (2 * sqrtT) - r * K * ert * nd2) / 365; 
    } else {
        const n_d1 = normalCDF(-d1);
        const n_d2 = normalCDF(-d2);
        price = K * ert * n_d2 - S * n_d1;
        delta = nd1 - 1;
        rho = -K * T * ert * n_d2;
        theta = (- (S * npd1 * sigma) / (2 * sqrtT) + r * K * ert * n_d2) / 365; 
    }

    const gamma = npd1 / (S * sigma * sqrtT);
    const vega = (S * npd1 * sqrtT) / 100; 

    return { price, delta, gamma, vega, theta, rho };
};

export interface MatrixCell {
    spotChange: number; 
    volChange: number; 
    price: number;
    pnl: number;
}

export const generateSensitivityMatrix = (
    baseParams: { S: number, K: number, T: number, r: number, sigma: number, type: 'call' | 'put' },
    entryPrice: number
): MatrixCell[][] => {
    const rows = [];
    const volShifts = [10, 5, 0, -5, -10]; 
    const spotShifts = [-10, -5, 0, 5, 10];

    for (let vShift of volShifts) {
        const row: MatrixCell[] = [];
        for (let sShift of spotShifts) {
            const newSpot = baseParams.S * (1 + sShift / 100);
            const newVol = Math.max(0.01, baseParams.sigma + (vShift / 100)); 

            const res = calculateBlackScholes(newSpot, baseParams.K, baseParams.T, baseParams.r, newVol, baseParams.type);
            const pnl = res.price - entryPrice;

            row.push({
                spotChange: sShift,
                volChange: vShift,
                price: res.price,
                pnl: pnl
            });
        }
        rows.push(row);
    }
    return rows;
};

// 1. Generate Geometric Brownian Motion
export const generateSyntheticHistory = (startPrice: number, volatility: number, drift: number, days: number): number[] => {
    const prices = [startPrice];
    const dt = 1 / 252; 
    
    for (let i = 1; i < days; i++) {
        const prev = prices[i - 1];
        const shock = boxMullerTransform(); 
        const change = prev * (drift * dt + volatility * Math.sqrt(dt) * shock);
        prices.push(prev + change);
    }
    return prices;
};

const boxMullerTransform = (): number => {
    const u = 1 - Math.random(); 
    const v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};

// 2. Calculate Daily Returns
export const calculateReturns = (prices: number[]): number[] => {
    const returns: number[] = [];
    for (let i = 1; i < prices.length; i++) {
        returns.push(Math.log(prices[i] / prices[i - 1]));
    }
    return returns;
};

// 3. Calculate Annualized Volatility
export const calculateVolatility = (returns: number[]): number => {
    const n = returns.length;
    if (n === 0) return 0;
    
    const mean = returns.reduce((a, b) => a + b, 0) / n;
    const variance = returns.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / (n - 1);
    
    return Math.sqrt(variance) * Math.sqrt(252) * 100;
};

// 4. Calculate Annualized Return
export const calculateAnnualizedReturn = (prices: number[]): number => {
    const first = prices[0];
    const last = prices[prices.length - 1];
    const days = prices.length;
    return (Math.pow(last / first, 252 / days) - 1) * 100;
};

// 5. Calculate Correlation
export const calculateCorrelation = (returnsA: number[], returnsB: number[]): number => {
    const n = Math.min(returnsA.length, returnsB.length);
    if (n === 0) return 0;

    const meanA = returnsA.reduce((a, b) => a + b, 0) / n;
    const meanB = returnsB.reduce((a, b) => a + b, 0) / n;

    let numerator = 0;
    let denomA = 0;
    let denomB = 0;

    for (let i = 0; i < n; i++) {
        const diffA = returnsA[i] - meanA;
        const diffB = returnsB[i] - meanB;
        numerator += diffA * diffB;
        denomA += diffA * diffA;
        denomB += diffB * diffB;
    }

    return numerator / Math.sqrt(denomA * denomB);
};

// 6. Technical Indicators
export const calculateSMA = (prices: number[], period: number): (number | null)[] => {
    const sma: (number | null)[] = [];
    for (let i = 0; i < prices.length; i++) {
        if (i < period - 1) {
            sma.push(null);
            continue;
        }
        const slice = prices.slice(i - period + 1, i + 1);
        const sum = slice.reduce((a, b) => a + b, 0);
        sma.push(sum / period);
    }
    return sma;
};

export const calculateRSI = (prices: number[], period: number = 14): (number | null)[] => {
    const rsi: (number | null)[] = [];
    let gains = 0;
    let losses = 0;

    for (let i = 0; i < prices.length; i++) {
        if (i === 0) {
            rsi.push(null);
            continue;
        }

        const change = prices[i] - prices[i - 1];
        const gain = change > 0 ? change : 0;
        const loss = change < 0 ? Math.abs(change) : 0;

        if (i < period) {
            gains += gain;
            losses += loss;
            rsi.push(null);
        } else if (i === period) {
            let avgGain = gains / period;
            let avgLoss = losses / period;
            const rs = avgGain / avgLoss;
            rsi.push(100 - (100 / (1 + rs)));
        } else {
            const slice = prices.slice(i - period, i);
            let pGains = 0;
            let pLosses = 0;
            for(let j=1; j<slice.length; j++) {
                const c = slice[j] - slice[j-1];
                if(c > 0) pGains += c;
                else pLosses += Math.abs(c);
            }
            const avgGain = pGains / period;
            const avgLoss = pLosses / period;
            const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
            rsi.push(100 - (100 / (1 + rs)));
        }
    }
    return rsi;
};

// 7. Backtest Engine
export const runBacktest = (prices: number[], smaShort: number[], smaLong: number[]): { equityCurve: number[], trades: number } => {
    let cash = 100000;
    let shares = 0;
    const equityCurve = [];
    let tradeCount = 0;

    for(let i=0; i<prices.length; i++) {
        const price = prices[i];
        const s = smaShort[i];
        const l = smaLong[i];

        if (s && l) {
            if (s > l && shares === 0) {
                shares = cash / price;
                cash = 0;
                tradeCount++;
            } else if (s < l && shares > 0) {
                cash = shares * price;
                shares = 0;
                tradeCount++;
            }
        }

        const totalValue = cash + (shares * price);
        equityCurve.push(totalValue);
    }

    return { equityCurve, trades: tradeCount };
};

// --- FACTOR ANALYSIS ENGINE (Simplified) ---

export interface FactorExposure {
    factor: string;
    exposure: number; // Beta or Z-Score (-2 to +2)
}

export const calculateFactorExposure = (
    holdings: { symbol: string; weight: number; beta?: number }[]
): FactorExposure[] => {
    // In a real system, we would regress portfolio returns against factor indices (MTUM, VLUE, USMV).
    // Here, we simulate factor loadings based on asset characteristics (mocked by ticker).
    
    let momentumScore = 0;
    let valueScore = 0;
    let volatilityScore = 0;
    let qualityScore = 0;
    let sizeScore = 0; // Small Cap bias

    holdings.forEach(pos => {
        const w = pos.weight / 100;
        
        // Mock Factor Characteristics based on symbol
        if (pos.symbol === 'NVDA' || pos.symbol === 'AMD') {
            momentumScore += w * 1.8;
            volatilityScore += w * 1.5;
            valueScore += w * -0.5; // Expensive
        } else if (pos.symbol === 'AAPL' || pos.symbol === 'MSFT') {
            qualityScore += w * 1.5;
            momentumScore += w * 0.8;
            volatilityScore += w * 0.9;
        } else if (pos.symbol === 'JPM' || pos.symbol === 'XOM') {
            valueScore += w * 1.2;
            qualityScore += w * 1.0;
            volatilityScore += w * 0.7;
        } else if (pos.symbol === 'TSLA') {
            momentumScore += w * 1.5;
            volatilityScore += w * 2.0;
            qualityScore += w * 0.2;
        } else {
            // Generic
            momentumScore += w * 0.5;
            valueScore += w * 0.5;
            volatilityScore += w * 0.8;
        }
    });

    return [
        { factor: 'Momentum', exposure: parseFloat(momentumScore.toFixed(2)) },
        { factor: 'Value', exposure: parseFloat(valueScore.toFixed(2)) },
        { factor: 'Volatility', exposure: parseFloat(volatilityScore.toFixed(2)) },
        { factor: 'Quality', exposure: parseFloat(qualityScore.toFixed(2)) },
        { factor: 'Size', exposure: parseFloat(sizeScore.toFixed(2)) }, // Currently 0 in this simple logic
    ];
};